import { SECTION_KEYS, type ApplyPart, type AssetDetail, type ContextRunDetail, type SectionKey, type SubjectDraft } from '@lastrouter/contracts';
import { Alert, Button, Card, CardContent, CardDescription, Check, ChevronDown, cn, Field, focusRing, Textarea } from '@lastrouter/ui';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { errorMessage, useApiMutation } from '../../api/hooks.js';
import { StatusBadge } from '../../components/common.js';
import { formatDateTime } from '../../lib/format.js';
import { lineDiff } from './logic.js';
import { ASSET_QUERIES } from './versions.js';

/*
 * Aba Contexto: o que o chat lê, seção por seção, com o que vale hoje (a versão publicada) ao lado da proposta
 * (a da última geração e o rascunho aberto). Aceitar a proposta usa o mesmo aplicar da geração (apply-run). Editar é
 * direto na seção: sem rascunho aberto, o Salvar da página abre um. Nenhum caminho de gravação novo.
 */

type Mode = 'substituir' | 'acrescentar';
type Tag = 'suggested' | 'changed' | 'same';
const TAG_TONE = { suggested: 'accent', changed: 'warning', same: 'neutral' } as const;

/** Aceitar partes da proposta: abre o rascunho quando não há e aplica as partes, uma chamada por modo. */
function useAccept(detail: AssetDetail, run: ContextRunDetail | null) {
  const { t } = useTranslation();
  const create = useApiMutation('createVersion', [...ASSET_QUERIES]);
  const apply = useApiMutation('applyContextRun', [...ASSET_QUERIES]);
  async function accept(steps: { parts: ApplyPart[]; mode: Mode }[]) {
    if (!run) return;
    try {
      let d =
        detail.draft ??
        (await create.mutateAsync({ params: { id: detail.asset.id }, body: { from_version_id: detail.published?.id ?? null, reason: t('assets.review.draftReason') } }));
      for (const s of steps) {
        if (!s.parts.length) continue;
        d = await apply.mutateAsync({ params: { id: d.version.id }, body: { run_id: run.run.id, parts: s.parts, mode: s.mode, expected_updated_at: d.version.updated_at } });
      }
      toast.success(t('assets.generate.applied'));
    } catch (err) {
      toast.error(errorMessage(t, err));
    }
  }
  return { accept, busy: create.isPending || apply.isPending };
}

function Text({ label, text }: { label: string; text: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <span className="text-[length:var(--lr-text-sm)] font-medium text-fg-secondary">{label}</span>
      {text.trim() ? (
        <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-[var(--lr-radius-md)] bg-sunken p-3 font-mono text-[length:var(--lr-text-xs)]">{text}</pre>
      ) : (
        <p className="text-[length:var(--lr-text-sm)] text-fg-muted">{t('assets.review.emptyText')}</p>
      )}
    </div>
  );
}

/** No celular, no lugar das duas colunas: o texto de hoje com o que sai riscado e o que entra marcado. */
function Diff({ before, after }: { before: string; after: string }) {
  const { t } = useTranslation();
  const lines = lineDiff(before, after);
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <span className="text-[length:var(--lr-text-sm)] font-medium text-fg-secondary">{t('assets.review.whatChanges')}</span>
      <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-[var(--lr-radius-md)] bg-sunken p-3 font-mono text-[length:var(--lr-text-xs)]">
        {lines.map((l, i) => (
          <span
            key={i}
            className={cn('block', l.kind === 'add' && 'bg-success-surface text-success', l.kind === 'del' && 'bg-danger-surface text-danger line-through')}
          >
            <span aria-hidden="true">{l.kind === 'add' ? '+ ' : l.kind === 'del' ? '- ' : '  '}</span>
            {l.kind === 'same' ? null : <span className="sr-only">{t(`assets.review.diff.${l.kind}`)}: </span>}
            {l.text || ' '}
          </span>
        ))}
      </pre>
    </div>
  );
}

/** Primeira linha com texto, para a prévia da seção fechada. */
const firstLine = (s: string | null | undefined) => (s ?? '').split('\n').map((l) => l.replace(/^[-*#\s]+/, '').trim()).find(Boolean) ?? '';

/**
 * Sanfona de uma parte do contexto: título, selo e, fechada, uma linha de prévia. Abre e fecha com a altura e o fade do
 * menu (lr-collapse); fechada, o conteúdo sai do Tab (inert).
 */
function Fold({ id, title, tag, help, preview, open, onToggle, children }: { id: string; title: string; tag: Tag; help: string; preview: string; open: boolean; onToggle: () => void; children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <Card>
      <h2 className="m-0">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={`${id}-corpo`}
          onClick={onToggle}
          className={cn('flex w-full items-center gap-3 rounded-[var(--card-radius)] px-[var(--card-padding)] py-3 text-left hover:bg-sunken/60', focusRing)}
        >
          <span className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="flex flex-wrap items-center gap-2">
              <span className="text-[length:var(--lr-text-md)] font-semibold leading-tight">{title}</span>
              <StatusBadge tone={TAG_TONE[tag]}>{t(`assets.review.tag.${tag}`)}</StatusBadge>
            </span>
            {open ? null : <span className="truncate text-[length:var(--lr-text-sm)] font-normal text-fg-muted">{preview || t('assets.review.emptyText')}</span>}
          </span>
          <ChevronDown aria-hidden="true" className={cn('lr-nav-toggle-icon size-4 shrink-0 text-fg-muted', !open && '-rotate-90')} />
        </button>
      </h2>
      <div id={`${id}-corpo`} className="lr-collapse" data-open={open} inert={!open}>
        <div>
          <CardContent className="flex flex-col gap-3 pt-0">
            <CardDescription>{help}</CardDescription>
            {children}
          </CardContent>
        </div>
      </div>
    </Card>
  );
}

interface SectionProps {
  k: SectionKey;
  today: string;
  /** O texto em edição (rascunho ou publicada, com a edição local); null quando o app ainda não tem contexto. */
  draft: string | null;
  /** O texto da geração quando ele difere do que o app tem agora; null sem proposta. */
  suggestion: string | null;
  canAccept: boolean;
  onAccept: () => void;
  onChange: (text: string) => void;
  open: boolean;
  onToggle: () => void;
}

function SectionReview({ k, today, draft, suggestion, canAccept, onAccept, onChange, open, onToggle }: SectionProps) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const changed = draft !== null && draft !== today;
  const tag: Tag = suggestion !== null ? 'suggested' : changed ? 'changed' : 'same';
  // A coluna da proposta é o editor do rascunho quando ele já mudou ou a pessoa pediu para editar; senão, o texto da geração.
  const showEditor = draft !== null && (changed || editing);
  const proposal = showEditor ? draft : suggestion;
  return (
    <Fold id={`secao-${k}`} title={t(`assets.sections.${k}`)} tag={tag} help={t(`assets.sectionHelp.${k}`)} preview={firstLine(draft ?? today)} open={open} onToggle={onToggle}>
        {proposal === null ? (
          <Text label={t('assets.review.today')} text={today} />
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="hidden min-w-0 lg:block">
              <Text label={t('assets.review.today')} text={today} />
            </div>
            <div className="min-w-0 lg:hidden">
              <Diff before={today} after={proposal} />
            </div>
            {showEditor ? (
              <div className="flex min-w-0 flex-col gap-2">
                <Field id={`secao-${k}`} label={t('assets.review.draft')}>
                  <Textarea rows={12} className="font-mono" value={draft} onChange={(e) => onChange(e.target.value)} maxLength={50_000} />
                </Field>
                {suggestion !== null ? (
                  <details className="rounded-[var(--lr-radius-md)] border border-border p-3">
                    <summary className="cursor-pointer text-[length:var(--lr-text-sm)] text-accent-text">{t('assets.review.runSuggests')}</summary>
                    <pre className="mt-2 max-h-80 overflow-auto whitespace-pre-wrap break-words font-mono text-[length:var(--lr-text-xs)]">{suggestion}</pre>
                  </details>
                ) : null}
              </div>
            ) : (
              <div className="hidden min-w-0 lg:block">
                <Text label={t('assets.review.proposal')} text={proposal} />
              </div>
            )}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {suggestion !== null ? (
            <Button size="sm" variant="primary" disabled={!canAccept} onClick={onAccept}>
              <Check aria-hidden="true" />
              {t('assets.review.accept')}
            </Button>
          ) : null}
          {showEditor || draft === null ? null : (
            <Button size="sm" onClick={() => setEditing(true)}>
              {t('assets.review.edit')}
            </Button>
          )}
          {changed ? (
            <Button
              size="sm"
              onClick={() => {
                onChange(today);
                setEditing(false);
              }}
            >
              {t('assets.review.keep')}
            </Button>
          ) : null}
        </div>
    </Fold>
  );
}

type Item = { id: string; nome: string };
const names = (xs: Item[]) => xs.map((x) => x.nome).join(', ');
const ids = (xs: Item[]) => xs.map((x) => x.id).join('|');

function SemanticSide({ label, s }: { label: string; s: { metricas: Item[]; dimensoes: Item[] } | null }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <span className="text-[length:var(--lr-text-sm)] font-medium text-fg-secondary">{label}</span>
      {s && (s.metricas.length || s.dimensoes.length) ? (
        <div className="flex flex-col gap-1 rounded-[var(--lr-radius-md)] bg-sunken p-3 text-[length:var(--lr-text-sm)]">
          <p className="font-medium">{t('assets.review.semanticCounts', { metrics: s.metricas.length, dimensions: s.dimensoes.length })}</p>
          {s.metricas.length ? <p className="break-words text-fg-secondary">{names(s.metricas)}</p> : null}
          {s.dimensoes.length ? <p className="break-words text-fg-secondary">{names(s.dimensoes)}</p> : null}
        </div>
      ) : (
        <p className="text-[length:var(--lr-text-sm)] text-fg-muted">{t('assets.review.emptyText')}</p>
      )}
    </div>
  );
}

function SemanticReview({
  today,
  current,
  hasDraft,
  fresh,
  run,
  canAccept,
  onAccept,
  editor,
  open,
  onToggle,
}: {
  today: SubjectDraft | null;
  current: SubjectDraft | null;
  hasDraft: boolean;
  /** Itens da geração que o app ainda não tem. */
  fresh: Item[];
  run: ContextRunDetail | null;
  canAccept: boolean;
  onAccept: (mode: Mode) => void;
  editor: ReactNode;
  open: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const s = run?.suggestion ?? null;
  const changed = hasDraft && (ids(today?.metricas ?? []) !== ids(current?.metricas ?? []) || ids(today?.dimensoes ?? []) !== ids(current?.dimensoes ?? []));
  const tag: Tag = fresh.length ? 'suggested' : changed ? 'changed' : 'same';
  return (
    <Fold
      id="semantica"
      title={t('assets.semantic')}
      tag={tag}
      help={t('assets.review.semanticHelp')}
      preview={current ? t('assets.review.semanticCounts', { metrics: current.metricas.length, dimensions: current.dimensoes.length }) : ''}
      open={open}
      onToggle={onToggle}
    >
        <div className={cn('grid gap-4', (changed || s) && 'lg:grid-cols-2')}>
          <SemanticSide label={t('assets.review.today')} s={today} />
          {changed ? <SemanticSide label={t('assets.review.draft')} s={current} /> : s ? <SemanticSide label={t('assets.review.proposal')} s={s} /> : null}
        </div>
        {s ? (
          <>
            <p className="text-[length:var(--lr-text-sm)] text-fg-secondary">
              {fresh.length ? t('assets.review.semanticFresh', { count: fresh.length, names: names(fresh) }) : t('assets.review.semanticNoFresh')}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="primary" disabled={!canAccept || !fresh.length} onClick={() => onAccept('acrescentar')}>
                <Check aria-hidden="true" />
                {t('assets.review.addMissing')}
              </Button>
              <Button size="sm" disabled={!canAccept} onClick={() => onAccept('substituir')}>
                {t('assets.review.replaceAll')}
              </Button>
            </div>
          </>
        ) : null}
        {editor ? (
          <details className="rounded-[var(--lr-radius-md)] border border-border p-3">
            <summary className="cursor-pointer text-[length:var(--lr-text-sm)] text-accent-text">{hasDraft ? t('assets.review.editSemantic') : t('assets.review.seeSemantic')}</summary>
            <div className="mt-3">{editor}</div>
          </details>
        ) : null}
    </Fold>
  );
}

export interface ContextTabProps {
  detail: AssetDetail;
  /** O contexto em edição: o rascunho com as mudanças locais, ou a versão publicada sem rascunho. */
  working: SubjectDraft | null;
  dirty: boolean;
  /** A geração que terminou bem; null sem geração. */
  run: ContextRunDetail | null;
  onSection: (k: SectionKey, text: string) => void;
  /** Motivo e Salvar do rascunho. */
  saveBar: ReactNode;
  /** O editor da camada semântica, montado pela página para não perder o que foi digitado. */
  semanticEditor: ReactNode;
}

export function ContextTab({ detail, working, dirty, run, onSection, saveBar, semanticEditor }: ContextTabProps) {
  const { t } = useTranslation();
  const { draft, published } = detail;
  const { accept, busy } = useAccept(detail, run);
  const canAccept = !busy && !dirty;
  const today = published?.subject ?? null;
  const current = working;
  const suggestion = run?.suggestion ?? null;

  const suggestionOf = (k: SectionKey): string | null => {
    const s = suggestion?.secoes[k];
    return s !== undefined && s.trim() !== (current?.secoes[k] ?? '').trim() ? s : null;
  };
  const pendingSections = SECTION_KEYS.filter((k) => suggestionOf(k) !== null);
  const has = new Set([...(current?.metricas ?? []), ...(current?.dimensoes ?? [])].map((x) => x.id));
  const fresh: Item[] = suggestion ? [...suggestion.metricas, ...suggestion.dimensoes].filter((x) => !has.has(x.id)) : [];
  const pending = pendingSections.length + (fresh.length ? 1 : 0);

  // Abertas de saída: as partes com proposta ou com mudança; sem nenhuma, a primeira.
  const changedSections = SECTION_KEYS.filter((k) => working && (working.secoes[k] ?? '') !== (today?.secoes[k] ?? ''));
  const [open, setOpen] = useState<Set<string>>(() => {
    const first = new Set<string>([...pendingSections, ...changedSections, ...(fresh.length ? ['semantica'] : [])]);
    return first.size ? first : new Set<string>(['contexto']);
  });
  const toggle = (k: string) =>
    setOpen((o) => {
      const n = new Set(o);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });
  const allKeys = [...SECTION_KEYS, 'semantica'];
  const allOpen = allKeys.every((k) => open.has(k));

  return (
    <div className="flex flex-col gap-4">
      {draft || dirty ? saveBar : <Alert tone="info" title={working ? t('assets.noDraft') : t('assets.nothing')} />}
      {suggestion && run ? (
        <Card>
          <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <p className="font-medium">{t('assets.review.fromRun', { when: formatDateTime(run.run.started_at), user: run.run.started_by })}</p>
              <p className="text-[length:var(--lr-text-sm)] text-fg-secondary">
                {pending ? t('assets.review.pending', { count: pending }) : t('assets.review.nothingPending')}
              </p>
            </div>
            {pending ? (
              <div>
                <Button
                  variant="primary"
                  disabled={!canAccept}
                  onClick={() =>
                    void accept([
                      { parts: pendingSections.map((k) => `secoes:${k}` as ApplyPart), mode: 'substituir' },
                      { parts: fresh.length ? ['metricas', 'dimensoes'] : [], mode: 'acrescentar' },
                    ])
                  }
                >
                  <Check aria-hidden="true" />
                  {t('assets.review.acceptAll')}
                </Button>
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}
      {dirty && pending ? <Alert tone="warning" title={t('assets.review.saveFirst')} /> : null}
      {draft?.issues.length ? (
        <Alert tone="warning" title={t('assets.issues')}>
          <ul className="list-disc pl-5">
            {draft.issues.map((i) => (
              <li key={i}>{i}</li>
            ))}
          </ul>
        </Alert>
      ) : null}
      <div className="flex justify-end">
        <Button size="sm" variant="ghost" onClick={() => setOpen(allOpen ? new Set() : new Set(allKeys))}>
          {allOpen ? t('assets.review.closeAll') : t('assets.review.openAll')}
        </Button>
      </div>
      {SECTION_KEYS.map((k) => (
        <SectionReview
          open={open.has(k)}
          onToggle={() => toggle(k)}
          key={k}
          k={k}
          today={today?.secoes[k] ?? ''}
          draft={working ? (working.secoes[k] ?? '') : null}
          suggestion={suggestionOf(k)}
          canAccept={canAccept}
          onAccept={() => void accept([{ parts: [`secoes:${k}`], mode: 'substituir' }])}
          onChange={(text) => onSection(k, text)}
        />
      ))}
      <SemanticReview
        today={today}
        current={current}
        hasDraft={Boolean(working)}
        fresh={fresh}
        run={run}
        canAccept={canAccept}
        onAccept={(mode) => void accept([{ parts: ['metricas', 'dimensoes'], mode }])}
        editor={semanticEditor}
        open={open.has('semantica')}
        onToggle={() => toggle('semantica')}
      />
    </div>
  );
}
